import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Chip,
  useMediaQuery,
  useTheme,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Images } from "../../utils/Images";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getUserSubscriptions } from "../../api/services/userService";
import CancelSubscriptionModal from "../../components/common/CancelSubscriptionModal";

const Subscriptions = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery('(min-width:600px) and (max-width:900px)');
  const isSmallMediumScreen = useMediaQuery('(min-width:600px) and (max-width:800px)');
  const [showFilters, setShowFilters] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });

  // iOS Safari does not show placeholder for input[type="date"], and can behave oddly unless toggled from text->date on focus
  const isIOS = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }, []);

  const getDateFieldType = (value) => (isIOS && !value ? "text" : "date");
  const handleDateFocus = (e) => {
    if (isIOS) e.target.type = "date";
  };
  const handleDateBlur = (e) => {
    if (isIOS && !e.target.value) e.target.type = "text";
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "pm" : "am";
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} ${displayHours}:${displayMinutes}${ampm}`;
  };

  // Format currency helper
  const formatCurrency = (amount, currency = "INR") => {
    if (!amount) return "₹0";
    const numAmount = parseFloat(amount);
    if (currency === "INR") {
      return `₹${numAmount.toLocaleString("en-IN")}`;
    }
    return `${currency} ${numAmount.toLocaleString("en-US")}`;
  };

  // Fetch REAL subscriptions (subscriptions rows with live statuses — not transactions)
  const fetchSubscriptions = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const response = await getUserSubscriptions(user.id);
      const rows = Array.isArray(response?.data) ? response.data : [];

      let mapped = rows.map((s) => ({
        id: s.id,
        date: formatDate(s.createdAt),
        createdAtRaw: s.createdAt,
        subscriptionId: `#${s.subscriptionId}`,
        recurringAmount: formatCurrency(s.amount, "INR"),
        plan: s.category_label,
        status: s.status_label, // Active | Paused | Halted | Cancelled | Completed | Pending
        transactionId: s.subscriptionId, // used by the cancel flow
        originalData: s,
      }));

      // Same UI filters, applied client-side over the real subscription rows
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        mapped = mapped.filter(
          (r) =>
            r.subscriptionId.toLowerCase().includes(q) ||
            String(r.plan || "").toLowerCase().includes(q)
        );
      }
      if (status) {
        mapped = mapped.filter((r) => r.status.toLowerCase() === status.toLowerCase());
      }
      if (fromDate) {
        mapped = mapped.filter((r) => new Date(r.createdAtRaw) >= new Date(fromDate));
      }
      if (toDate) {
        mapped = mapped.filter((r) => new Date(r.createdAtRaw) <= new Date(`${toDate}T23:59:59`));
      }

      const totalRecords = mapped.length;
      const totalPages = Math.max(1, Math.ceil(totalRecords / rowsPerPage));
      const start = page * rowsPerPage;

      setSubscriptions(mapped.slice(start, start + rowsPerPage));
      setPagination({
        currentPage: page + 1,
        totalPages,
        totalRecords,
        limit: rowsPerPage,
        hasNextPage: page + 1 < totalPages,
        hasPrevPage: page > 0,
      });
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, fromDate, toDate, status, user?.id]);

  // Handle cancel subscription — the modal itself owns the reason form, the API call,
  // and the "kept"/"cancelled" result screens (see CancelSubscriptionModal)
  const handleCancelClick = (subscription) => {
    setSelectedSubscription(subscription.originalData || subscription);
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setSelectedSubscription(null);
  };

  // Use subscriptions directly from API (API handles filtering and pagination)
  const filteredSubscriptions = subscriptions;
  const paginatedSubscriptions = subscriptions; // API already returns paginated results

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleBackToMenu = () => {
    navigate('/profile');
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      {/* Mobile header with back button */}
            {isMobile && (
              <Box
                onClick={handleBackToMenu}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "20px",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 18L9 12L15 6"
                      stroke="#1D1D1B"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                    color: "#1D1D1B",
                  }}
                >
                 Subscriptions
                </Typography>
              </Box>
            )}
      {!isMobile && (
        <Paper elevation={2} sx={{ overflow: isMediumScreen ? 'auto' : 'visible', boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1) !important" }}>
            <Box
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                p: 3,
                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1) !important", // Force shadow to always appear
              }}
            >
             {/* Header and Search Section */}
             <Box
               sx={{
                 display: "flex",
                 flexDirection: isSmallMediumScreen ? "column" : "row",
                 justifyContent: isSmallMediumScreen ? "flex-start" : "end",
                 alignItems: isSmallMediumScreen ? "flex-start" : "center",
                 mb: "20px",
                 gap: isSmallMediumScreen ? "16px" : "16px",
                 minWidth: 0, // Allow shrinking
                 overflow: "hidden", // Prevent overflow
                 flexWrap: "wrap", // Allow wrapping
               }}
             >
               {/* Header for desktop and larger medium screens */}
               {!isSmallMediumScreen && (
                 <Typography
                   component="h1"
                   sx={{
                     fontWeight: 600,
                     fontStyle: "SemiBold",
                     fontSize: "22px",
                     lineHeight: "22.34px",
                     letterSpacing: "0%",
                     color: "#1D1D1B",
                     flexShrink: 0,
                     margin: 0,
                     padding: 0,
                     order: 1, // Always stay on the left
                   }}
                 >
                   My Subscriptions
                 </Typography>
               )}

               {/* Header for 800px to 600px screens with filter button */}
               {isSmallMediumScreen && (
                 <Box sx={{ 
                   display: "flex", 
                   justifyContent: "space-between", 
                   alignItems: "center", 
                   width: "100%",
                   order: 1,
                 }}>
                   <Typography
                     component="h1"
                     sx={{
                       fontWeight: 600,
                       fontStyle: "SemiBold",
                       fontSize: "22px",
                       lineHeight: "22.34px",
                       letterSpacing: "0%",
                       color: "#1D1D1B",
                       flexShrink: 0,
                       margin: 0,
                       padding: 0,
                     }}
                   >
                     My Subscriptions
                   </Typography>
                   <IconButton
                     onClick={() => setShowFilters(!showFilters)}
                     sx={{
                       backgroundColor: "#1D1D1B !important",
                       borderRadius: "8px",
                       width: "40px",
                       height: "40px",
                     }}
                   >
                     <Box
                       component="img"
                       src={showFilters ? Images.whiteClose : Images.filter}
                       alt={showFilters ? "Close" : "Filter"}
                       sx={{ width: "20px", height: "20px" }}
                     />
                   </IconButton>
                 </Box>
               )}

               {/* Search Field - Top Right */}
               {!isSmallMediumScreen && (
                <Box sx={{ 
                  flexShrink: 0,
                  order: 2, // Always stay on the right
                  marginLeft: "auto",
                  flex: "0 1 auto", // Don't grow by default
                  maxWidth: "300px", // Limit default width
                  '@media (max-width: 1250px)': {
                    flex: "1 1 auto", // Fill space when filters come down
                    maxWidth: "100%",
                  },
                }}>
                  <TextField
                    placeholder="Search by Subscription ID"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    sx={{
                      width: "100%", // Fill the container
                      minWidth: "220px",
                      '@media (min-width: 1250px)': {
                        maxWidth: "300px", // Limit width on large screens
                      },
                      '@media (max-width: 1250px)': {
                        maxWidth: "none", // No limit when filters come down
                      },
                       "& .MuiOutlinedInput-root": {
                         borderRadius: "8px",
                         paddingTop: "14px",
                         paddingRight: "24px",
                         paddingBottom: "14px",
                         paddingLeft: "24px",
                         backgroundColor: "#FFFFFF",
                         border: "1px solid #BEBFD0",
                         "& fieldset": {
                           border: "none",
                         },
                         "&:hover fieldset": {
                           border: "none",
                         },
                         "&.Mui-focused fieldset": {
                           border: "none",
                         },
                       },
                       "& .MuiInputBase-input": {
                         padding: 0,
                         margin: 0,
                         fontWeight: 500,
                         fontStyle: "Medium",
                         fontSize: "14px",
                         lineHeight: "100%",
                         letterSpacing: "0%",
                         verticalAlign: "middle",
                         color: "#686866",
                         "&::placeholder": {
                           fontWeight: 500,
                           fontStyle: "Medium",
                           fontSize: "14px",
                           lineHeight: "100%",
                           letterSpacing: "0%",
                           verticalAlign: "middle",
                           color: "#686866",
                           opacity: 1,
                         },
                       },
                     }}
                   />
                 </Box>
               )}

               {/* Date Fields and Status Filter - Separate Box */}
               {!isSmallMediumScreen && (
                 <Box
                   sx={{
                     display: "flex",
                     gap: "8px",
                     alignItems: "center",
                     flexWrap: "nowrap", // Keep them together
                     flexShrink: 0, // Don't allow this box to shrink
                     order: 3, // Will come after search
                   }}
                 >
                 {/* From Date */}
                <TextField
                  type={getDateFieldType(fromDate)}
                  placeholder="From date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  onFocus={handleDateFocus}
                  onBlur={handleDateBlur}
                  variant="outlined"
                  sx={{
                     minWidth: isMediumScreen ? "120px" : "140px",
                     maxWidth: isMediumScreen ? "140px" : "160px",
                     flex: "0 1 auto", // Allow shrinking but not growing
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      paddingTop: isMediumScreen ? "10px" : "13px",
                      paddingRight: isMediumScreen ? "16px" : "24px",
                      paddingBottom: isMediumScreen ? "10px" : "13px",
                      paddingLeft: isMediumScreen ? "16px" : "24px",
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #BEBFD0",
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: 0,
                      margin: 0,
                      fontWeight: 500,
                      fontStyle: "Medium",
                      fontSize: "14px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      verticalAlign: "middle",
                      color: "#686866",
                      "&::placeholder": {
                        fontWeight: 500,
                        fontStyle: "Medium",
                        fontSize: "14px",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                        verticalAlign: "middle",
                        color: "#686866",
                        opacity: 1,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      display: "none",
                    },
                  }}
                />

                {/* To Date */}
                <TextField
                  type={getDateFieldType(toDate)}
                  placeholder="To date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  onFocus={handleDateFocus}
                  onBlur={handleDateBlur}
                  variant="outlined"
                  sx={{
                     minWidth: isMediumScreen ? "120px" : "140px",
                     maxWidth: isMediumScreen ? "140px" : "160px",
                     flex: "0 1 auto", // Allow shrinking but not growing
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      paddingTop: isMediumScreen ? "10px" : "13px",
                      paddingRight: isMediumScreen ? "16px" : "24px",
                      paddingBottom: isMediumScreen ? "10px" : "13px",
                      paddingLeft: isMediumScreen ? "16px" : "24px",
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #BEBFD0",
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: 0,
                      margin: 0,
                      fontWeight: 500,
                      fontStyle: "Medium",
                      fontSize: "14px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      verticalAlign: "middle",
                      color: "#686866",
                      "&::placeholder": {
                        fontWeight: 500,
                        fontStyle: "Medium",
                        fontSize: "14px",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                        verticalAlign: "middle",
                        color: "#686866",
                        opacity: 1,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      display: "none",
                    },
                  }}
                />

                {/* Status Filter */}
                <FormControl sx={{ 
                     minWidth: isMediumScreen ? "120px" : "140px",
                     maxWidth: isMediumScreen ? "140px" : "160px",
                     flex: "0 1 auto", // Allow shrinking but not growing
                }}>
                  <Select
                    value={status}
                    displayEmpty
                    onChange={(e) => setStatus(e.target.value)}
                     sx={{
                       borderRadius: "8px",
                       paddingTop: isMediumScreen ? "10px" : "15px",
                       paddingRight: isMediumScreen ? "16px" : "24px",
                       paddingBottom: isMediumScreen ? "10px" : "9px",
                       paddingLeft: isMediumScreen ? "16px" : "24px",
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #BEBFD0",
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "& .MuiSelect-select": {
                        padding: 0,
                        margin: 0,
                        fontWeight: 500,
                        fontStyle: "Medium",
                        fontSize: "14px",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                        verticalAlign: "middle",
                        color: status ? "#1D1D1B" : "#686866",
                      },
                      "& .MuiSelect-icon": {
                        color: "#686866",
                      },
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: "#686866" }}>Status</span>;
                      }
                      return selected;
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
                 </Box>
               )}

               {/* Filter Section for 800px to 600px screens */}
               {isSmallMediumScreen && showFilters && (
                 <Box sx={{ 
                   order: 2,
                   width: "100%",
                   display: "flex",
                   flexDirection: "column",
                   gap: "12px",
                 }}>
                   <TextField
                     placeholder="Search By Subscription ID"
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}
                     variant="outlined"
                     sx={{
                       width: "100%",
                       "& .MuiOutlinedInput-root": {
                         borderRadius: "8px",
                         backgroundColor: "#fff",
                         border: "1px solid #DDD",
                         padding: "12px 20px 12px 16px",
                         paddingRight: "0px",
                         height: "40px",
                         "& fieldset": { border: "none" },
                       },
                       "& .MuiInputBase-input": {
                         fontSize: "14px",
                         color: "#686866",
                         padding: 0,
                       },
                     }}
                     InputProps={{
                       startAdornment: (
                         <InputAdornment position="start">
                           <Box
                             component="img"
                             src={Images.search}
                             alt="Search"
                             sx={{ width: "18px", height: "18px" }}
                           />
                         </InputAdornment>
                       ),
                     }}
                   />
                   
                   <Box sx={{ 
                     display: "flex", 
                     gap: "8px", 
                     flexWrap: "wrap",
                   }}>
                     <TextField
                       type={getDateFieldType(fromDate)}
                       placeholder="From date"
                       value={fromDate}
                       onChange={(e) => setFromDate(e.target.value)}
                       onFocus={handleDateFocus}
                       onBlur={handleDateBlur}
                       variant="outlined"
                       sx={{
                         flex: "1",
                         minWidth: "140px",
                         "& .MuiOutlinedInput-root": {
                           borderRadius: "8px",
                           backgroundColor: "#fff",
                           border: "1px solid #DDD",
                           padding: "12px 16px",
                           height: "40px",
                           "& fieldset": { border: "none" },
                         },
                         "& .MuiInputBase-input": {
                           fontSize: "14px",
                           color: "#686866",
                           padding: 0,
                         },
                         "& .MuiInputLabel-root": {
                           display: "none",
                         },
                       }}
                     />
                     <TextField
                       type={getDateFieldType(toDate)}
                       placeholder="To date"
                       value={toDate}
                       onChange={(e) => setToDate(e.target.value)}
                       onFocus={handleDateFocus}
                       onBlur={handleDateBlur}
                       variant="outlined"
                       sx={{
                         flex: "1",
                         minWidth: "140px",
                         "& .MuiOutlinedInput-root": {
                           borderRadius: "8px",
                           backgroundColor: "#fff",
                           border: "1px solid #DDD",
                           padding: "12px 16px",
                           height: "40px",
                           "& fieldset": { border: "none" },
                         },
                         "& .MuiInputBase-input": {
                           fontSize: "14px",
                           color: "#686866",
                           padding: 0,
                         },
                         "& .MuiInputLabel-root": {
                           display: "none",
                         },
                       }}
                     />
                     <FormControl sx={{ 
                       flex: "1",
                       minWidth: "140px",
                     }}>
                       <Select
                         value={status}
                         displayEmpty
                         onChange={(e) => setStatus(e.target.value)}
                         sx={{
                           height: "40px",
                           backgroundColor: "#fff",
                           border: "1px solid #DDD",
                           borderRadius: "8px",
                           "& .MuiOutlinedInput-notchedOutline": {
                             border: "none",
                           },
                           "& .MuiSelect-select": {
                             fontSize: "14px",
                             color: status ? "#1D1D1B" : "#686866",
                             padding: "12px 16px",
                           },
                         }}
                         renderValue={(selected) => {
                           if (!selected) {
                             return <span style={{ color: "#686866" }}>Status</span>;
                           }
                           return selected;
                         }}
                       >
                         <MenuItem value="">All</MenuItem>
                         <MenuItem value="active">Active</MenuItem>
                         <MenuItem value="cancelled">Cancelled</MenuItem>
                       </Select>
                     </FormControl>
                   </Box>
                 </Box>
               )}
            </Box>

            {/* Table */}
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: "4px",
                boxShadow: "none",
                border: "none",
                overflowX: isMediumScreen ? "auto" : "hidden",
              }}
            >
              <Table sx={{ minWidth: isMediumScreen ? 600 : 'auto' }}>
                <TableHead>
                  <TableRow
                    sx={{ 
                      backgroundColor: "#F5F8ED", 
                      padding: "30px",
                      '& .MuiTableCell-head': {
                        fontWeight: 600,
                        fontStyle: "SemiBold",
                        fontSize: isMediumScreen ? "14px" : "16px",
                        lineHeight: "16px",
                        letterSpacing: "0%",
                        color: "#1D1D1B",
                        borderBottom: "1px solid #E0E0E0",
                        whiteSpace: "nowrap",
                        padding: isMediumScreen ? "10px 12px" : "16px",
                      }
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontStyle: "SemiBold",
                        fontSize: "16px",
                        lineHeight: "16px",
                        letterSpacing: "0%",
                        color: "#1D1D1B",
                        borderBottom: "1px solid #E0E0E0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Subscription Date and Time
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontStyle: "SemiBold",
                        fontSize: "16px",
                        lineHeight: "16px",
                        letterSpacing: "0%",
                        color: "#1D1D1B",
                        borderBottom: "1px solid #E0E0E0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Subscription ID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontStyle: "SemiBold",
                        fontSize: "16px",
                        lineHeight: "16px",
                        letterSpacing: "0%",
                        color: "#1D1D1B",
                        borderBottom: "1px solid #E0E0E0",
                      }}
                    >
                      Recurring Amount
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontStyle: "SemiBold",
                        fontSize: "16px",
                        lineHeight: "16px",
                        letterSpacing: "0%",
                        color: "#1D1D1B",
                        borderBottom: "1px solid #E0E0E0",
                      }}
                    >
                      Subscription Status
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontStyle: "SemiBold",
                        fontSize: "16px",
                        lineHeight: "16px",
                        letterSpacing: "0%",
                        color: "#1D1D1B",
                        borderBottom: "1px solid #E0E0E0",
                      }}
                    >
                      Cancel Subscription
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : paginatedSubscriptions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography sx={{ color: "#686866" }}>
                          No subscriptions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedSubscriptions.map((subscription) => (
                      <TableRow
                        key={subscription.id}
                        sx={{
                          "&:hover": { backgroundColor: "#F8F9FA" },
                          "&:last-child td": { borderBottom: 0 },
                          height: '60px',
                          '& .MuiTableCell-body': {
                            padding: isMediumScreen ? "8px 12px" : "16px",
                            fontSize: isMediumScreen ? "14px" : "16px",
                          }
                        }}
                      >
                        <TableCell
                          sx={{
                            fontFamily: "Red Hat Display",
                            fontWeight: 500,
                            fontStyle: "Medium",
                            fontSize: "16px",
                            lineHeight: "16px",
                            letterSpacing: "0%",
                            color: "#1D1D1B",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {subscription.date}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Red Hat Display",
                            fontWeight: 500,
                            fontStyle: "Medium",
                            fontSize: "16px",
                            lineHeight: "16px",
                            letterSpacing: "0%",
                            color: "#4A4A49",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {subscription.subscriptionId}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Red Hat Display",
                            fontWeight: 500,
                            fontStyle: "Medium",
                            fontSize: "16px",
                            lineHeight: "16px",
                            letterSpacing: "0%",
                            color: "#1D1D1B",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {subscription.recurringAmount}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              fontFamily: "Red Hat Display",
                              fontWeight: 500,
                              fontStyle: "Medium",
                              fontSize: "13px",
                              lineHeight: "100%",
                              letterSpacing: "0%",
                              width: "89px",
                              background: "",
                              color: "white",
                              borderRadius: "50px",
                              textAlign: "center",
                              paddingY: "6px",
                              bgcolor:
                                subscription.status === "Active"
                                  ? "#29CC6A"
                                  : "#FF5858",
                            }}
                          >
                            {subscription.status}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Red Hat Display",
                            fontWeight: 500,
                            fontStyle: "Medium",
                            fontSize: "16px",
                            lineHeight: "16px",
                            letterSpacing: "0%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "200px",
                            width: "200px",
                            color: "#F04951",
                            cursor: subscription.status === "Active" ? "pointer" : "default",
                          }}
                          onClick={() => subscription.status === "Active" && handleCancelClick(subscription)}
                        >
                          {subscription.status === "Active" ? "Cancel" : "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
                flexWrap: "nowrap", // Don't wrap the main container
                gap: 2,
              }}
            >
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 1,
                flexWrap: "wrap", // Allow wrapping inside this container
                minWidth: 0, // Allow container to shrink
                mr: 2, // Margin to separate from pagination
              }}>
                {/* Rows per page dropdown - always visible */}
                <Box sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1,
                  flexShrink: 0, // Don't shrink this part
                }}>
                  <Typography variant="body2" sx={{ color: "#5E5E5E", whiteSpace: "nowrap" }}>
                    Rows per page:
                  </Typography>
                  <Select
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    size="small"
                    sx={{
                      minWidth: "60px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#E0E0E0",
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </Box>
                
                {/* Row count text - can wrap down */}
                <Typography variant="body2" sx={{ 
                  color: "#5E5E5E",
                  whiteSpace: "nowrap",
                  flexShrink: 1, // Allow this to shrink
                }}>
                  Row {pagination.totalRecords > 0 
                    ? `${(page * rowsPerPage) + 1}-${Math.min((page + 1) * rowsPerPage, pagination.totalRecords)} of ${pagination.totalRecords} rows`
                    : filteredSubscriptions.length > 0 
                      ? `${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, filteredSubscriptions.length)} of ${filteredSubscriptions.length} rows`
                      : "0-0 of 0 rows"}
                </Typography>
              </Box>

              {/* Pagination buttons - won't wrap down */}
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 1,
                flexWrap: "nowrap",
                flexShrink: 0 // Never shrink this
              }}>
                {/* Back Button */}
                <Box
                  onClick={() => {
                    if (page > 0) setPage(page - 1);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #E0E0E0",
                    backgroundColor: "#FFFFFF",
                    cursor: page === 0 ? "not-allowed" : "pointer",
                    opacity: page === 0 ? 0.5 : 1,
                    "&:hover": {
                      backgroundColor: page === 0 ? "#FFFFFF" : "#F5F5F5",
                    },
                  }}
                >
                  {/* Left Chevron SVG */}
                  <Box
                    sx={{
                      width: "16px",
                      height: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 12L6 8L10 4"
                        stroke="#5E5E5E"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#5E5E5E",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    Back
                  </Typography>
                </Box>

                {/* Page Numbers */}
                {(() => {
                  const totalPages = Math.max(1, pagination.totalPages || Math.ceil(filteredSubscriptions.length / rowsPerPage) || 1);
                  const currentPage = pagination.currentPage || 1;
                  
                  // Generate page numbers to display
                  const getPageNumbers = () => {
                    if (totalPages === 0) return [1];
                    if (totalPages === 1) return [1];
                    
                    const pages = [];
                    const currentPageIndex = currentPage - 1; // Convert to 0-based
                    
                    if (currentPageIndex === 0) {
                      // On first page, show up to 3 pages
                      for (let i = 1; i <= Math.min(3, totalPages); i++) {
                        pages.push(i);
                      }
                    } else if (currentPageIndex === totalPages - 1) {
                      // On last page, show previous pages
                      const start = Math.max(1, totalPages - 2);
                      for (let i = start; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      // On middle pages, show current and previous pages
                      const start = Math.max(1, currentPageIndex);
                      for (let i = start; i <= Math.min(start + 2, totalPages); i++) {
                        pages.push(i);
                      }
                    }
                    return pages;
                  };
                  
                  return getPageNumbers().map((pageNum) => {
                    const pageIndex = pageNum - 1; // Convert to 0-based
                    return (
                      <Box
                        key={pageNum}
                        onClick={() => setPage(pageIndex)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "32px",
                          height: "32px",
                          borderRadius: "6px",
                          backgroundColor:
                            page === pageIndex ? "#1D1D1B" : "#FFFFFF",
                          border:
                            page === pageIndex ? "none" : "1px solid #E0E0E0",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor:
                              page === pageIndex ? "#2A2A2A" : "#F5F5F5",
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontSize: "14px",
                            color: page === pageIndex ? "#FFFFFF" : "#5E5E5E",
                          }}
                        >
                          {pageNum}
                        </Typography>
                      </Box>
                    );
                  });
                })()}

                {/* Next Button */}
                <Box
                  onClick={() => {
                    const totalPages = Math.max(1, pagination.totalPages || Math.ceil(filteredSubscriptions.length / rowsPerPage) || 1);
                    if (page < totalPages - 1) setPage(page + 1);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #E0E0E0",
                    backgroundColor: "#FFFFFF",
                    cursor: page >= (pagination.totalPages || Math.ceil(filteredSubscriptions.length / rowsPerPage) || 1) - 1 ? "not-allowed" : "pointer",
                    opacity: page >= (pagination.totalPages || Math.ceil(filteredSubscriptions.length / rowsPerPage) || 1) - 1 ? 0.5 : 1,
                    "&:hover": {
                      backgroundColor: page >= (pagination.totalPages || Math.ceil(filteredSubscriptions.length / rowsPerPage) || 1) - 1 ? "#FFFFFF" : "#F5F5F5",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#5E5E5E",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    Next
                  </Typography>
                  {/* Right Chevron SVG */}
                  <Box
                    sx={{
                      width: "16px",
                      height: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 4L10 8L6 12"
                        stroke="#5E5E5E"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}
      {isMobile && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              mb: "20px",
            }}
          >
            <TextField
              placeholder="Search By Subscription ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  border: "1px solid #DDD",
                  padding: "12px 20px 12px 16px",
                  paddingRight: "0px",
                  height: "40px",
                  "& fieldset": { border: "none" },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  color: "#686866",
                  padding: 0,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="img"
                      src={Images.search}
                      alt="Search"
                      sx={{ width: "18px", height: "18px" }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                backgroundColor: "#1D1D1B !important",
                borderRadius: "8px",
                width: "40px",
                height: "40px",
              }}
            >
              <Box
                component="img"
                src={showFilters ? Images.whiteClose : Images.filter}
                alt={showFilters ? "Close" : "Filter"}
                sx={{ width: "20px", height: "20px" }}
              />
            </IconButton>
          </Box>
          
          {/* Filter Section for Mobile */}
          {showFilters && (
            <Box sx={{ 
              mb: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}>
              <Box sx={{ 
                display: "flex", 
                gap: "8px", 
                flexWrap: "wrap",
              }}>
                <TextField
                  type={getDateFieldType(fromDate)}
                  placeholder="From date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  onFocus={handleDateFocus}
                  onBlur={handleDateBlur}
                  variant="outlined"
                  sx={{
                    flex: "1",
                    minWidth: "140px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      border: "1px solid #DDD",
                      padding: "12px 16px",
                      height: "40px",
                      "& fieldset": { border: "none" },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                      color: "#686866",
                      padding: 0,
                    },
                    "& .MuiInputLabel-root": {
                      display: "none",
                    },
                  }}
                />
                <TextField
                  type={getDateFieldType(toDate)}
                  placeholder="To date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  onFocus={handleDateFocus}
                  onBlur={handleDateBlur}
                  variant="outlined"
                  sx={{
                    flex: "1",
                    minWidth: "140px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      border: "1px solid #DDD",
                      padding: "12px 16px",
                      height: "40px",
                      "& fieldset": { border: "none" },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                      color: "#686866",
                      padding: 0,
                    },
                    "& .MuiInputLabel-root": {
                      display: "none",
                    },
                  }}
                />
                <FormControl sx={{ 
                  flex: "1",
                  minWidth: "140px",
                }}>
                  <Select
                    value={status}
                    displayEmpty
                    onChange={(e) => setStatus(e.target.value)}
                    sx={{
                      height: "40px",
                      backgroundColor: "#fff",
                      border: "1px solid #DDD",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "& .MuiSelect-select": {
                        fontSize: "14px",
                        color: status ? "#1D1D1B" : "#686866",
                        padding: "12px 16px",
                      },
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: "#686866" }}>Status</span>;
                      }
                      return selected;
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}

          {/* Subscription Cards - Mobile View */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : paginatedSubscriptions.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography sx={{ color: "#686866" }}>
                  No subscriptions found
                </Typography>
              </Box>
            ) : (
              paginatedSubscriptions
                .map((subscription) => (
              <Box
                key={subscription.id}
                sx={{
                  border: "1px solid rgba(0, 0, 0, 0.10)",
                  borderRadius: "16px",
                  p: "14px 16px",
                  backgroundColor: "#fff",
                  boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
                }}
              >
                {/* Subscription ID */}
                <Box
                  sx={{
                    backgroundColor: "#F5F8ED",
                    borderRadius: "6px",
                    display: "inline-block",
                    padding: "10px",
                    width: "100%",
                    mb: "14px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#6A994E",
                    }}
                  >
                     Subscription ID - <span style={{color: "#1D1D1B", fontWeight: 700,}}>{subscription.subscriptionId}</span>
                  </Typography>
                </Box>

                {/* Date and Status */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",}}>
                  <Box>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#686866",
                    mb: "10px",
                  }}
                >
                  Subscription Date and Time
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#0B1215",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                    width: "100%",
                  }}
                >
                  {subscription.date}
                </Typography>
                </Box>
                {subscription.status === "Active" && (
                  <Box>
                    <Box sx={{ 
                              width:"79px",
                              padding:"6px 0px",
                              textAlign:"center",
                              borderRadius:"50.198px",
                              color:"#fff",
                              fontSize:"12px",
                              fontWeight:600,
                              backgroundColor: "#29CC6A"
                            }}> {subscription.status}</Box>
                  </Box>
                )}
                </Box>
                <Divider sx={{ marginBlock: "16px" }} />
                {/* Amount & Cancel */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#686866",
                        fontWeight: 500,
                        mb: "10px",
                      }}
                    >
                      Recurring Amount
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#1D1D1B",
                      }}
                    >
                      {subscription.recurringAmount}
                    </Typography>
                  </Box>
                   <Box
                      sx={{
                        width: "1px",
                        height: "40px",
                        backgroundColor: "#E0E0E0",
                        mx: 2,
                      }}
                    />
                  <Box>
                    {subscription.status === "Active" ? (
                      <Box
                        onClick={() => handleCancelClick(subscription)}
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#686866",
                          padding: "8px 16px",
                          borderRadius: "6px",
                          border: "1px solid #E0E0E0",
                          backgroundColor: "transparent",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "#F5F5F5",
                          },
                        }}
                      >
                        Cancel
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#fff",
                          padding: "6px 0px",
                          borderRadius: "50.198px",
                          backgroundColor: "#FF5757",
                          textAlign: "center",
                          width: "79px",
                        }}
                      >
                        Cancelled
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
                ))
            )}
          </Box>

          {/* Mobile Pagination */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: 3,
            alignItems: 'center',
            gap: 2
          }}>
            {/* Back Button */}
            <Box
              onClick={() => setPage(Math.max(0, page - 1))}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                border: "1px solid #E0E0E0",
                backgroundColor: "#FFFFFF",
                cursor: page === 0 ? "not-allowed" : "pointer",
                opacity: page === 0 ? 0.5 : 1,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 12L6 8L10 4" stroke="#5E5E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Box>

            {/* Page Indicator */}
            <Typography sx={{ 
              fontSize: '14px',
              fontWeight: 500,
              color: '#1D1D1B'
            }}>
              {(pagination.totalPages || Math.ceil(filteredSubscriptions.length / rowsPerPage) || 1) === 0 ? 0 : page + 1} / {Math.max(1, pagination.totalPages || Math.ceil(filteredSubscriptions.length / rowsPerPage) || 1)}
            </Typography>

            {/* Next Button */}
            <Box
              onClick={() => {
                const totalPages = Math.max(1, pagination.totalPages || Math.ceil(filteredSubscriptions.length / rowsPerPage) || 1);
                if (page < totalPages - 1) setPage(page + 1);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                border: "1px solid #E0E0E0",
                backgroundColor: "#FFFFFF",
                cursor: page >= (pagination.totalPages || Math.ceil(filteredSubscriptions.length / rowsPerPage) || 1) - 1 ? "not-allowed" : "pointer",
                opacity: page >= (pagination.totalPages || Math.ceil(filteredSubscriptions.length / rowsPerPage) || 1) - 1 ? 0.5 : 1,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 4L10 8L6 12" stroke="#5E5E5E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Box>
          </Box>
        </>
      )}

      {/* Cancel Subscription modal — reason capture + kept/cancelled result screens */}
      <CancelSubscriptionModal
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        subscription={selectedSubscription}
        onCancelled={fetchSubscriptions}
      />
    </Box>
  );
};

export default Subscriptions;
